// client/src/components/paymentBylaws/PaymentBylawList.js
import React, { useState, useEffect, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import {
  Container,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  IconButton,
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,  
  Chip,
  Alert,
  Snackbar,
  CircularProgress,
  Tooltip
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Check as CheckIcon,
  ContentCopy as CopyIcon,
  ViewList as ViewListIcon,
  Lock as LockIcon,
  LockOpen as LockOpenIcon,
  CheckCircle as CheckCircleIcon,
  Delete as DeleteIcon

} from '@mui/icons-material';

import { AuthContext } from '../../contexts/AuthContext';
import { LocaleContext } from '../../contexts/LocaleContext';
import paymentBylawService from '../../services/paymentBylawService';

const PaymentBylawList = () => {
  const { t, i18n } = useTranslation();
  const { isAdmin } = useContext(AuthContext);
  const { direction } = useContext(LocaleContext);
  const [bylaws, setBylaws] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null); 

  // Current language
  const lang = i18n.language;

  // Load bylaws on component mount
  useEffect(() => {
    loadBylaws();
  }, []);

  // Function to load bylaws
  const loadBylaws = async () => {
    try {
      setLoading(true);
      const response = await paymentBylawService.getPaymentBylaws();
      setBylaws(response.data);
      setError('');
    } catch (err) {
      setError(t('errorLoadingBylaws'));
    } finally {
      setLoading(false);
    }
  };

  // Handle approve bylaw

  const handleApproveBylaw = async (id) => {
    try {
      await paymentBylawService.approvePaymentBylaw(id);
      setSuccess(t('bylawApprovedSuccess'));
      loadBylaws(); // Reload bylaws after approval
    } catch (err) {
      setError(t('errorApprovingBylaw'));
    }
  };
  
  const handleReopenBylaw = async (id) => {
    try {
      await paymentBylawService.reopenForEdit(id);
      setSuccess(t('bylawReopenedSuccess'));
      loadBylaws(); // Reload bylaws after reopening
    } catch (err) {
      console.log('errorReopeningBylaw  --->' , err)  
      setError(t('errorReopeningBylaw'));
    }
  };

  const navigate = useNavigate(); // Make sure this hook is called

const handleViewTransactions = (bylawId, isReadOnly) => {
  navigate(`/payment-bylaws/${bylawId}/transactions`, { 
    state: { readOnly: isReadOnly } 
  });
};


  // Handle payment item deletion
  const handleDeleteClick = (item) => {
    console.log('  Bylaw to be deleted   ', item);
    setItemToDelete(item);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    try {
      await paymentBylawService.deletePaymentBylaw(itemToDelete.id);
      setSuccess(t('paymentItemDeletedSuccess'));
      loadBylaws();
    } catch (err) {
      setError(t('errorDeletingPaymentItem'));
    } finally {
      setDeleteDialogOpen(false);
      setItemToDelete(null);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
    setItemToDelete(null);
  };

  // Handle close alert
  const handleCloseAlert = () => {
    setSuccess('');
    setError('');
  };

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString();
  };

  return (
    <Container maxWidth="lg" sx={{ mt: 4 }}>
      <Typography variant="h4" gutterBottom>
        {t('paymentBylaws')}
      </Typography>

      {/* Alert messages */}
      <Snackbar 
        open={!!success} 
        autoHideDuration={6000} 
        onClose={handleCloseAlert}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert onClose={handleCloseAlert} severity="success" sx={{ width: '100%' }}>
          {success}
        </Alert>
      </Snackbar>

      <Snackbar 
        open={!!error} 
        autoHideDuration={6000} 
        onClose={handleCloseAlert}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert onClose={handleCloseAlert} severity="error" sx={{ width: '100%' }}>
          {error}
        </Alert>
      </Snackbar>

      {/* Add Bylaw button */}
      <Box sx={{ mb: 2, textAlign: direction === 'ltr' ? 'right' : 'left' }}>
        <Button 
          variant="contained" 
          color="primary" 
          startIcon={<AddIcon />}
          component={RouterLink}
          to="/payment-bylaws/create"
        >
          {t('addBylaw')}
        </Button>
      </Box>

      {/* Bylaws Table */}
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
          <CircularProgress />
        </Box>
      ) : bylaws.length === 0 ? (
        <Paper sx={{ p: 2, textAlign: 'center' }}>
          <Typography variant="body1">
            {t('noBylawsFound')}
          </Typography>
        </Paper>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>{t('name')}</TableCell>
                <TableCell>{t('type')}</TableCell>
                <TableCell>{t('faculty')}</TableCell>
                <TableCell>{t('expiration')}</TableCell>
                <TableCell align="center">{t('status')}</TableCell>
                <TableCell align="center">{t('actions')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {bylaws.map((bylaw) => (
                <TableRow key={bylaw.id}>
                  <TableCell>{bylaw.name}</TableCell>
                  <TableCell>{t(bylaw.bylaw_type)}</TableCell>
                  <TableCell>
                    {bylaw.faculty ? bylaw.faculty[`name_${lang}`] : t('notApplicable')}
                  </TableCell>
                  <TableCell>{formatDate(bylaw.expired_at)}</TableCell>
                  <TableCell align="center">
                    <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
                      {bylaw.is_last && (
                        <Chip 
                          label={t('current')} 
                          color="primary" 
                          size="small"
                        />
                      )}
                      <Chip 
                        label={bylaw.is_approved ? t('approved') : t('pending')} 
                        color={bylaw.is_approved ? 'success' : 'warning'}
                        size="small"
                      />
                    </Box>
                  </TableCell>
                  <TableCell align="center">
                    {/* Approve button (only for admins and unapproved bylaws) */}
                    {/* isAdmin() && !bylaw.is_approved && (
                      <IconButton
                        color="success"
                        onClick={() => handleApproveBylaw(bylaw.id)}
                        title={t('approve')}
                      >
                        <CheckIcon />
                      </IconButton>
                    )*/}
                    
                    {/* Edit button (only for unapproved bylaws) */}
                    {!bylaw.is_approved && (
                      <IconButton
                        color="primary"
                        component={RouterLink}
                        to={`/payment-bylaws/edit/${bylaw.id}`}
                        title={t('edit')}
                      >
                        <EditIcon />
                      </IconButton>
                    )}



                    

  {/* Transactions list button */}
  <Tooltip title={t('transactionsList')}>
    <IconButton
      color="info"
      onClick={() => handleViewTransactions(bylaw.id, bylaw.is_approved)}
    >
      <ViewListIcon />
    </IconButton>
  </Tooltip>               
  


{/* Approve/Reopen button */}
{isAdmin() && (
    <Tooltip title={bylaw.is_approved ? t('reopenForEdit') : t('approveBylaw')}>
      <IconButton
        color={bylaw.is_approved ? "warning" : "success"}
        onClick={bylaw.is_approved ? 
          () => handleReopenBylaw(bylaw.id) : 
          () => handleApproveBylaw(bylaw.id)
        }
      >
        {bylaw.is_approved ? <LockOpenIcon /> : <CheckCircleIcon />}
      </IconButton>
    </Tooltip>
  )}
  

                      {/* Copy button */}
                      <IconButton
                      color="info"
                      component={RouterLink}
                      to={`/payment-bylaws/create?copyFrom=${bylaw.id}`}
                      title={t('copyFromExisting')}
                    >
                      <CopyIcon />
                    </IconButton>

                    {/* Edit button (only for unapproved bylaws) */}
                    {!bylaw.is_approved && (
                    <IconButton
                      color="error"
                      onClick={() => handleDeleteClick(bylaw)}
                    >
                      <DeleteIcon />
                    </IconButton>
                    )}

                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={handleDeleteCancel}
      >
        <DialogTitle>{t('confirmDelete')}</DialogTitle>
        <DialogContent>
          <DialogContentText>
          {t('deletePaymentItemConfirmation', { 
              name: itemToDelete?.name 
            })}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteCancel}>{t('cancel')}</Button>
          <Button onClick={handleDeleteConfirm} color="error" autoFocus>
            {t('delete')}
          </Button>
        </DialogActions>
      </Dialog>

    </Container>
  );
};

export default PaymentBylawList;